import { withNamespaces } from "react-i18next"
import { Badge, UncontrolledTooltip } from "reactstrap"
import { KYBScreeningDataSets } from "src/modules/3rd-party-management/constants"
import DateUtils from "src/services/utils/DateUtils"
import  { INTERNATIONAL_DATE_FORMAT } from 'src/common/constants';
import RelevancyActions from "./RelevancyActions"
import CopyableText from "../common/CopyableText";

const genders = {
    'male': 'Male',
    'female': 'Female'
}

const LinkedBusinessesCell = ({ t, businessesLinks = [] }) => {
    if (!businessesLinks.length) {
        return (
            <span className="text-muted">
                {t('No linked businesses')}
            </span>
        )
    }

    return (
        <div className="linked-businesses">
            {businessesLinks.map((biz, index) => (
                <div key={biz.id || index} className="linked-business-card">
                    <div className="biz-name">
                        {biz.name}
                    </div>

                    {biz.relationship && (
                        <div className="biz-relationship">
                            {biz.relationship}
                        </div>
                    )}

                    {typeof biz.ownershipPercentage === "number" && (
                        <div className="biz-ownership">
                            {t('Ownership')}: <strong>{biz.ownershipPercentage}%</strong>
                        </div>
                    )}

                    {biz.datasets?.length > 0 && (
                        <div className="biz-datasets">
                            {biz.datasets.map(code => (
                                <span key={code} className="dataset-badge">
                                    {KYBScreeningDataSets[code]?.title}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

const GeneralInfosTable = ({
    resultData,
    complianceType,
    t
}) => {
    const dateUtils = new DateUtils();
    const type = resultData?.type?.toLowerCase();

    const renderName = () => {
        const name = type === 'business' ? resultData?.name : [
            resultData?.firstName,
            resultData?.middleName,
            resultData?.lastName
        ].filter(Boolean).join(" ");

        return (
            <h5>
                {name}

                {(resultData?.datasets || []).map((datasetKey, index) => {
                    return (
                        <Badge
                            key={index}
                            id={`row-dataset-${datasetKey}`}
                            className="bg-info ms-1 me-1"
                        >
                            {t(KYBScreeningDataSets[datasetKey]?.title)}

                            <UncontrolledTooltip
                                target={`row-dataset-${datasetKey}`}
                            >
                                {t(KYBScreeningDataSets[datasetKey]?.description)}
                            </UncontrolledTooltip>
                        </Badge>
                    )
                })}
            </h5>
        )
    }

    return (
        <div className="sanction-table-container mb-4 bg-white">
            <table className="table table-striped">
                <tbody>
                    <tr>
                        <td colSpan='2'>
                            <div className="d-flex justify-content-between">
                                <div className="finding-content-title flex-column">
                                    {renderName()}

                                    <small className="mt-1">
                                        <b className="me-1">{t('ID')}:</b> 
                                        <CopyableText
                                            value={resultData.monitoringID}
                                            maxLength={20}
                                        />
                                    </small>
                                </div>

                                {complianceType === 'screening' && (
                                    <RelevancyActions
                                        complianceType="screening"
                                        id={resultData.id}
                                        isFalsePositive={resultData.isFalsePositive}
                                    />
                                )}
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <th>{t('Images')}</th>
                        <td>
                            {(resultData?.profileImages || []).length > 0 ? (
                                resultData.profileImages.map((profileImage, index) => {
                                    return (
                                        <img
                                            key={index}
                                            style={{
                                                maxWidth: '120px'
                                            }}
                                            src={profileImage}
                                            alt="profile"
                                            className="me-1"
                                        />
                                    )
                                })
                            ) : (
                                <span className="text-muted">{t('No Image')}</span>
                            )}
                        </td>
                    </tr>
                    <tr>
                        <th>{t('Notes')}</th>
                        <td>
                            {(resultData?.notes || []).length > 0 ? (
                                resultData.notes.map((note, index) => {
                                    return (
                                        <p
                                            key={index}
                                            className="mb-1"
                                        >
                                            {note?.value}
                                        </p>
                                    )
                                })
                            ) : (
                                <span className="text-muted">---</span>
                            )}
                        </td>
                    </tr>
                    {type !== 'business' && (
                        <tr>
                            <th>{t('Gender')}</th>
                            <td>
                                <p>
                                    {t(
                                        genders[resultData?.gender?.toLowerCase()] || 'Unknown'
                                    )}
                                </p>
                            </td>
                        </tr>
                    )}

                    <tr>
                        <th>{t('Aliases')}</th>
                        <td>
                            {(resultData?.aliases || []).length > 0 ? (
                                resultData.aliases.map((alias, index) => {
                                    return (
                                        <p
                                            key={index}
                                            className="mb-1"
                                        >
                                            {type === 'business' ? [
                                                alias.alias
                                            ] : [
                                                alias.firstName,
                                                alias.middleName,
                                                alias.lastName
                                            ].filter(Boolean).join(" ")}
                                        </p>
                                    )
                                })
                            ) : (
                                <span className="text-muted">---</span>
                            )}
                        </td>
                    </tr>
                    {type !== 'business' && (
                        <>
                            <tr>
                                <th>{t('Dates of Birth')}</th>
                                <td>
                                    {(resultData?.dateOfBirth || []).length > 0 ? (
                                        resultData.dateOfBirth.map((birthDate, index) => {
                                            return (
                                                <p
                                                    key={index}
                                                    className="mb-1"
                                                >
                                                    {dateUtils.convertDateToDate(
                                                        birthDate,
                                                        INTERNATIONAL_DATE_FORMAT
                                                    )}
                                                </p>
                                            )
                                        })
                                    ) : (
                                        <span className="text-muted">---</span>
                                    )}
                                </td>
                            </tr>

                            <tr>
                                <th>{t('Dates of Death')}</th>
                                <td>
                                    {(resultData?.datesOfDeathISO || []).length > 0 ? (
                                        resultData.datesOfDeathISO.map((deathDate, index) => {
                                            return (
                                                <p
                                                    key={index}
                                                    className="mb-1"
                                                >
                                                    {dateUtils.convertDateToDate(
                                                        deathDate,
                                                        INTERNATIONAL_DATE_FORMAT
                                                    )}
                                                </p>
                                            )
                                        })
                                    ) : (
                                        <span className="text-muted">---</span>
                                    )}
                                </td>
                            </tr>

                            <tr>
                                <th>
                                    {t('Status')}
                                </th>
                                <td>
                                    {resultData?.isDeceased ? (
                                        <span
                                            className="badge bg-secondary"
                                        >
                                            {t('Deceased')}
                                        </span>
                                    ) : '---'}
                                </td>
                            </tr>

                            <tr>
                                <th>{t('Nationalities')}</th>
                                <td>
                                    <p>
                                        {(resultData?.nationalitiesISOCodes || []).join(', ')}
                                    </p>
                                </td>
                            </tr>
                        </>
                    )}

                    <tr>
                        <th>{t('Identifiers')}</th>
                        <td>
                            {resultData?.identifiers?.length > 0 ? (
                                resultData.identifiers.map((ident, i) => (
                                    <div key={i}>
                                        <strong>{ident.category}</strong>:{" "}
                                        <span>
                                            {ident.value}
                                        </span>
                                    </div>
                                ))
                            ) : (
                                '---'
                            )}
                        </td>
                    </tr>
                    <tr>
                        <th>
                            {t('Contact Details')}
                        </th>
                        <td>
                            {resultData?.contactEntries?.length > 0 ? (
                                resultData.contactEntries.map((contact, i) => {
                                    return (
                                        <div key={i}>
                                            <strong>{contact.category}:</strong>{" "}
                                            <span>
                                                {contact.value}
                                            </span>
                                        </div>
                                    );
                                })
                            ) : (
                                '---'
                            )}
                        </td>
                    </tr>
                    <tr>
                        <th>{t('Addresses')}</th>
                        <td>
                            {(resultData?.addresses || []).length > 0 ? (
                                resultData.addresses.map((addr, i) => (
                                    <p key={i} className="mb-2">
                                        {addr.line1 && <span>{addr.line1}<br /></span>}
                                        {addr.line2 && <span>{addr.line2}<br /></span>}

                                        <span>
                                            {addr.city} {addr.postcode}
                                            <br />
                                        </span>

                                        {addr.county && <span>{addr.county}<br /></span>}

                                        <span>{addr.countryISOCode}</span>

                                        {addr.addressType && (
                                            <>
                                                <br />
                                                <small className="text-muted">
                                                    {addr.addressType} {t('Address')}
                                                </small>
                                            </>
                                        )}
                                    </p>
                                ))
                            ) : (
                                <span className="text-muted">---</span>
                            )}
                        </td>
                    </tr>
                    <tr>
                        <th>
                            {t('Linked Businesses')}
                        </th>
                        <td>
                            <LinkedBusinessesCell
                                businessesLinks={resultData?.businessLinks}
                                t={t}
                            />
                        </td>
                    </tr>
                    <tr>
                        <th>
                            {t("ID")}
                        </th>
                        <td>
                            <p className="mb-1">
                                <CopyableText
                                    value={resultData.monitoringID}
                                    maxLength={20}
                                />
                            </p>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    )
}

export default withNamespaces()(
    GeneralInfosTable
);